import { useSyncExternalStore } from 'react';
import {
  getIconDefinition,
  isDeferredIconPending,
  registerDeferredIconDefinitions,
} from '@/constants/iconMap.generated';

/**
 * Loads the deferred half of the icon map and re-renders the icons waiting on it.
 *
 * ## Why the map is split at all
 *
 * `iconMap.generated.js` used to be one module with 793 static imports. Because
 * `components/Icon.jsx` resolves icons by **runtime string**, every icon any screen might
 * name had to be present on every page — including icons referenced only from code that
 * already lives in an async chunk. Stubbing the map measured that at **171 KB gzip** of
 * the entry bundle, the largest single item in it.
 *
 * The generator now splits by surface: icons reachable on first paint stay eager, and the
 * rest — mostly ~260 catalog icons named by database records in `backend/scripts/seed` —
 * come from here.
 *
 * ## Why this is safe to defer
 *
 * `Icon` wraps every glyph in a `View` of the final width and height, and already
 * rendered nothing for an unknown name. So an icon that has not arrived occupies its
 * final box and leaves it empty: **no layout shift**, and no SEO exposure, since icons
 * carry no crawlable text. The visible cost is a brief empty square, not reflow.
 *
 * Two things keep that window short. `getIconDefinition` returns null rather than the
 * question-mark fallback for a pending name, so nothing flashes a wrong glyph first. And
 * `prefetchDeferredIcons` warms the chunk when the app goes idle, so by the time a user
 * reaches a catalog page the map is usually already there.
 */

let loadPromise = null;
let loaded = false;
const subscribers = new Set();

/**
 * Fetch the deferred map. Safe to call repeatedly and from any number of icons at once —
 * the promise is cached, so a page holding fifty pending icons issues one request.
 */
export function loadDeferredIcons() {
  if (loaded) return Promise.resolve();
  if (!loadPromise) {
    loadPromise = import('@/constants/iconMap.deferred.generated')
      .then((mod) => {
        registerDeferredIconDefinitions(mod.DEFERRED_ICON_DEFINITIONS || mod.default);
        loaded = true;
        // Bump before notifying so subscribers re-read a snapshot that already includes
        // the new definitions.
        version += 1;
        subscribers.forEach((notify) => notify());
      })
      .catch((err) => {
        // Not latched: a failed chunk fetch should not permanently blank every catalog
        // icon, and the next miss is a reasonable moment to try again.
        loadPromise = null;
        console.warn('icon registry: deferred icon map failed to load', err);
      });
  }
  return loadPromise;
}

/**
 * Warm the deferred chunk without blocking anything. Call once the shell is interactive;
 * `requestIdleCallback` keeps it off the critical path on browsers that have it.
 */
export function prefetchDeferredIcons() {
  if (loaded || loadPromise) return;
  const start = () => { loadDeferredIcons(); };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(start, { timeout: 3000 });
  } else if (typeof setTimeout === 'function') {
    setTimeout(start, 1200);
  }
}

// A counter is the snapshot rather than the definitions object, because the registry
// mutates the map in place. `useSyncExternalStore` compares snapshots by identity, so
// returning the object would look unchanged and skip the re-render.
let version = 0;

const subscribe = (notify) => {
  subscribers.add(notify);
  return () => { subscribers.delete(notify); };
};
const getSnapshot = () => version;
const getServerSnapshot = () => 0;

/**
 * Resolve an icon, loading the deferred map if this name lives there.
 *
 * Returns null while a deferred icon is in flight, which renders as an empty box of the
 * right size. Unknown names still fall back to the question mark exactly as before.
 */
export function useIconDefinition(name, variant = 'far') {
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const definition = getIconDefinition(name, variant);
  if (!definition && isDeferredIconPending(name, variant)) {
    // Kicked off during render on purpose: an effect would cost an extra frame per icon,
    // and this is an idempotent cache fill with no observable effect on this render.
    loadDeferredIcons();
  }
  return definition;
}
